import React, { useState } from "react";
import { Heading, Box, Card, Button } from "theme-ui";

import { Decimal, Decimalish, Difference, LiquityStoreState, SABLEStake } from "@liquity/lib-base";
import { useLiquitySelector } from "@liquity/lib-react";

import { COIN } from "../../strings";

import { Icon } from "../Icon";
import { EditableRow, StaticRow } from "../Trove/Editor";
import { LoadingOverlay } from "../LoadingOverlay";

import { useStakingView } from "./context/StakingViewContext";
import { useStakingContext } from "./context/StakingContext";
import SableBnbLpBalanceLabel from "./SableBnbLpBalanceLabel";
import useChain from "../../hooks/useChain";

const select = ({ sableBalance, totalStakedSABLE }: LiquityStoreState) => ({
  sableBalance,
  totalStakedSABLE
});

type StakingEditorProps = {
  children?: React.ReactNode;
  title: string;
  originalStake: SABLEStake;
  editedSABLE: Decimal;
  dispatch: (action: { type: "setStake"; newValue: Decimalish } | { type: "revert" }) => void;
};

export const StakingEditor: React.FC<StakingEditorProps> = ({
  children,
  title,
  originalStake,
  editedSABLE,
  dispatch
}) => {
  /** @todo should rename it as totalStakedSableBnbLp */
  const { totalStakedSABLE } = useLiquitySelector(select);
  const {
    userStakingData: { lpBalance: sableBnbLpBalance },
    pancakeSwapPool2Data: { lpfeeApr }
  } = useStakingContext();
  const { changePending } = useStakingView();
  const editingState = useState<string>();

  const edited = !editedSABLE.eq(originalStake.stakedSABLE);

  const chain = useChain();
  const currencySymbol = chain.nativeCurrency.symbol;
  const lpSymbol = chain.isStakeLPSupported ? `SABLE-${chain.nativeCurrency.symbol}` : "SABLE";
  const maxAmount = originalStake.stakedSABLE.add(sableBnbLpBalance);
  const maxedOut = editedSABLE.eq(maxAmount);

  const totalStakedSableBnbLpAfterChange = totalStakedSABLE
    .sub(originalStake.stakedSABLE)
    .add(editedSABLE);

  const originalPoolShare = originalStake.stakedSABLE.mulDiv(100, totalStakedSABLE);
  const newPoolShare = editedSABLE.mulDiv(100, totalStakedSableBnbLpAfterChange);
  const poolShareChange =
    originalStake.stakedSABLE.nonZero && Difference.between(newPoolShare, originalPoolShare).nonZero;

  return (
    <Card>
      <Heading>
        {title}
        {edited && !changePending && (
          <Button
            variant="titleIcon"
            sx={{ ":enabled:hover": { color: "danger" } }}
            onClick={() => dispatch({ type: "revert" })}
          >
            <Icon name="history" size="lg" />
          </Button>
        )}
      </Heading>

      <Box sx={{ p: [2, 3], display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={{ flex: 1 }}>
        <EditableRow
          label="Stake"
          trailingLabel={<SableBnbLpBalanceLabel />}
          inputId="stake-sable"
          amount={editedSABLE.prettify()}
          maxAmount={maxAmount.toString()}
          maxedOut={maxedOut}
          unit={lpSymbol}
          {...{ editingState }}
          editedAmount={editedSABLE.toString(2)}
          setEditedAmount={newValue => dispatch({ type: "setStake", newValue })}
        />

        {newPoolShare.infinite ? (
          <StaticRow label="Pool share" inputId="stake-share" amount="N/A" />
        ) : (
          <StaticRow
            label="Pool share"
            inputId="stake-share"
            amount={newPoolShare.prettify(4)}
            pendingAmount={poolShareChange?.prettify(4).concat("%")}
            pendingColor={poolShareChange?.positive ? "success" : "danger"}
            unit="%"
          />
        )}

        {!originalStake.isEmpty && (
          <>
            <StaticRow
              label="Redemption gain"
              inputId="stake-gain-eth"
              amount={originalStake.collateralGain.prettify(4)}
              color={originalStake.collateralGain.nonZero && "success"}
              unit={currencySymbol}
            />

            <StaticRow
              label="Issuance gain"
              inputId="stake-gain-usds"
              amount={originalStake.usdsGain.prettify()}
              color={originalStake.usdsGain.nonZero && "success"}
              unit={COIN}
            />
            {chain.isStakeLPSupported && (
              <StaticRow
                label="LP fee APR"
                inputId="lp-fee-apr"
                amount={lpfeeApr?.toFixed(2) || "-"}
                color="success"
                unit="%"
              />
            )}
          </>
        )}

        {originalStake.isEmpty && chain.isStakeLPSupported && (
          <>
            <StaticRow
              label="LP fee APR"
              inputId="lp-fee-apr"
              amount={lpfeeApr?.toFixed(2) || "-"}
              color="success"
              unit="%"
            />
          </>
        )}
        </Box>

        {children}
      </Box>

      {changePending && <LoadingOverlay />}
    </Card>
  );
};
